// MoreSheet — bottom sheet do "Mais" da BottomNav (mobile). Reúne os itens de
// navegação que não couberam no rodapé (overflow por papel) + Ajustes, tema e Sair.
// Mecânica de <dialog> nativo copiada do Drawer do CRM: showModal, close adiado
// pra animação, backdrop click e Escape de graça.
import { useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAppSelector } from '@/app/store/hooks';
import { ROUTES } from '@/app/router/paths';
import { selectRole, useLogout } from '@/features/auth';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { LogoutIcon, SettingsIcon } from '@/components/icons';
import { useIsMobile } from '@/lib/hooks/useMediaQuery';
import { cn } from '@/lib/utils/cn';
import { mobileNavForRole } from './navItems';
import styles from './MoreSheet.module.css';

// Casa com --modal-fade-duration (180ms) — adia o close() pro slide-out terminar.
const FADE_MS = 180;

interface Props {
  open: boolean;
  onClose: () => void;
}

export function MoreSheet({ open, onClose }: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const role = useAppSelector(selectRole);
  const { logout, isLoading } = useLogout();
  const isMobile = useIsMobile();
  const location = useLocation();

  // Itens de overflow: os do papel que não couberam no rodapé (mesma partição).
  const overflow = role ? mobileNavForRole(role).overflow : [];

  // Abre/fecha o <dialog> nativo espelhando a prop `open`.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    const clearCloseTimer = () => {
      if (closeTimer.current !== null) {
        clearTimeout(closeTimer.current);
        closeTimer.current = null;
      }
    };

    if (open) {
      clearCloseTimer();
      if (!dialog.open) dialog.showModal();
      dialog.dataset.state = 'open';
    } else if (dialog.open) {
      dialog.dataset.state = 'closing';
      const reduce =
        typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      closeTimer.current = setTimeout(
        () => {
          closeTimer.current = null;
          if (dialog.open) dialog.close();
        },
        reduce ? 0 : FADE_MS,
      );
    }

    return clearCloseTimer;
  }, [open]);

  // Escape/backdrop → onClose (mesma mecânica do Drawer).
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const onCloseEvt = () => onClose();
    const onClick = (e: MouseEvent) => {
      if (e.target === dialog) onClose();
    };
    dialog.addEventListener('close', onCloseEvt);
    dialog.addEventListener('click', onClick);
    return () => {
      dialog.removeEventListener('close', onCloseEvt);
      dialog.removeEventListener('click', onClick);
    };
  }, [onClose]);

  // Fecha ao navegar (a rota mudou enquanto o sheet estava aberto).
  useEffect(() => {
    if (open) onClose();
    // Só reage à mudança de rota; onClose/open são estáveis o suficiente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Some ao voltar pra desktop (evita sheet órfão num resize da janela).
  useEffect(() => {
    if (!isMobile && open) onClose();
  }, [isMobile, open, onClose]);

  return (
    <dialog ref={ref} className={styles.dialog} aria-label="Mais opções">
      <div className={styles.panel}>
        <div className={styles.handle} aria-hidden="true" />
        <nav className={styles.nav} aria-label="Mais navegação">
          {overflow.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => cn(styles.item, isActive && styles.active)}
            >
              <item.Icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className={styles.divider} role="separator" />
        <div className={styles.footer}>
          <NavLink
            to={ROUTES.config}
            className={({ isActive }) => cn(styles.item, isActive && styles.active)}
          >
            <SettingsIcon size={20} />
            <span>Ajustes</span>
          </NavLink>
          <div className={styles.themeRow}>
            <span>Tema</span>
            <ThemeToggle />
          </div>
          <button
            type="button"
            className={styles.logout}
            onClick={() => void logout()}
            disabled={isLoading}
          >
            <LogoutIcon size={20} />
            <span>Sair</span>
          </button>
        </div>
      </div>
    </dialog>
  );
}
